// ---------------------------------------------------------------------------
// Enlaces a GitHub de la app Horas Trabajadas. Lógica pura (se prueba sin
// montar componentes).
//
// Lo único que el contrato garantiza de un repo es la forma `owner/repo`
// (`CommitInput.repo`, `PullRequestRef.repo`): de ahí se arma la URL. La URL del
// PR (`PullRequestRef.url`) es OPCIONAL — sólo llega si la trajo quien ingirió el
// commit — así que cuando no viene se deriva de `owner/repo#n` en lugar de
// pintar un enlace muerto.
//
// Nada se enlaza a ciegas: un valor que no tenga forma de `owner/repo`, o una
// URL que no sea http(s), devuelve `null` y la UI lo pinta como texto plano.
// ---------------------------------------------------------------------------

/** Un segmento de ruta de GitHub (owner, repo): sin `/`, sin `:` y sin espacios. */
const SEGMENT = /^[A-Za-z0-9._-]+$/;

export const GITHUB_BASE = "https://github.com";

/** URL de un repositorio `owner/repo`, o `null` si no tiene esa forma. */
export function repoUrl(repo: string | null | undefined): string | null {
  const parts = String(repo ?? "").trim().split("/");
  if (parts.length !== 2 || !parts.every((p) => SEGMENT.test(p))) return null;
  return `${GITHUB_BASE}/${parts[0]}/${parts[1]}`;
}

/** Deja pasar sólo URLs http(s) (las que manda la API son de GitHub, pero no se asume). */
export function safeHttpUrl(url: string | null | undefined): string | null {
  const value = String(url ?? "").trim();
  return /^https?:\/\//i.test(value) ? value : null;
}

/**
 * URL de un commit: la que viajó en el commit y, si no viajó, la derivada del
 * primer repo donde se vio (el mismo SHA puede estar en varios forks).
 */
export function commitUrl(commit: { sha: string; repos?: string[]; url?: string | null }): string | null {
  const given = safeHttpUrl(commit?.url);
  if (given) return given;
  const base = repoUrl((commit?.repos ?? [])[0]);
  const sha = String(commit?.sha ?? "").trim();
  return base && /^[0-9a-f]{7,40}$/i.test(sha) ? `${base}/commit/${sha}` : null;
}

/**
 * URL de un pull request: la que viajó en el commit y, si no viajó, la derivada
 * de `owner/repo` + número.
 */
export function pullRequestUrl(pr: { repo: string; number: number; url?: string | null }): string | null {
  const given = safeHttpUrl(pr?.url);
  if (given) return given;
  const base = repoUrl(pr?.repo);
  const number = Number(pr?.number);
  return base && Number.isInteger(number) && number > 0 ? `${base}/pull/${number}` : null;
}
